import { asArray, asOptionalString, asRecord, asString, reportDtoAnomaly } from './common';
import type {
  InvoiceCoverage,
  InvoiceCoverageInvoice,
  InvoiceCoverageLine,
  InvoiceCoverageState,
} from '../types/invoiceCoverage';

const STATES: readonly string[] = ['NONE', 'PARTIAL', 'FULL'];

function quantity(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/**
 * `invoiceCoverage` of GET /orders/:id, GET /supplier-orders/:id and the
 * invoice correction state. Absent → null (unknown, not "no invoice"); an
 * unexpected shape is reported and treated as unknown.
 */
export function normalizeInvoiceCoverage(raw: unknown, entity: string): InvoiceCoverage | null {
  if (raw === undefined || raw === null) return null;
  const record = asRecord(raw);
  const state = asString(record?.state);
  if (!record || !STATES.includes(state)) {
    reportDtoAnomaly(entity, 'invoiceCoverage', raw);
    return null;
  }
  return {
    state: state as InvoiceCoverageState,
    invoices: asArray(record.invoices, (item): InvoiceCoverageInvoice | null => {
      const row = asRecord(item);
      if (!row?.id) return null;
      return {
        id: asString(row.id),
        number: asOptionalString(row.number) ?? null,
        recognitionMonth: asOptionalString(row.recognitionMonth) ?? null,
        corrected: row.corrected === true,
      };
    }).filter((row): row is InvoiceCoverageInvoice => row !== null),
    lines: asArray(record.lines, (item): InvoiceCoverageLine | null => {
      const row = asRecord(item);
      if (!row?.id) return null;
      return {
        id: asString(row.id),
        name: asOptionalString(row.name) ?? null,
        unit: asOptionalString(row.unit) ?? null,
        orderedQuantity: quantity(row.orderedQuantity),
        invoicedQuantity: quantity(row.invoicedQuantity),
        remainingQuantity: quantity(row.remainingQuantity),
      };
    }).filter((row): row is InvoiceCoverageLine => row !== null),
  };
}

/**
 * Invoiced share of the whole order in whole percent, for the
 * „Zafakturowane częściowo (x %)” marker. Each line counts at most its
 * ordered quantity (over-invoicing one line never hides another line's
 * remainder). Lines in one unit: Σ invoiced / Σ ordered; mixed units: mean of
 * the line shares. Rounded down, so a partial order never reads 100 %.
 * null when any line's quantities are unknown.
 */
export function invoiceCoveragePercent(coverage: InvoiceCoverage | null | undefined): number | null {
  if (!coverage || coverage.state === 'NONE' || !coverage.lines.length) return null;
  const lines = coverage.lines;
  if (lines.some((line) => line.orderedQuantity == null || line.invoicedQuantity == null || line.orderedQuantity <= 0)) return null;
  const invoiced = (line: InvoiceCoverageLine) => Math.min(line.invoicedQuantity!, line.orderedQuantity!);
  const share = new Set(lines.map((line) => line.unit)).size === 1
    ? lines.reduce((sum, line) => sum + invoiced(line), 0) / lines.reduce((sum, line) => sum + line.orderedQuantity!, 0)
    : lines.reduce((sum, line) => sum + invoiced(line) / line.orderedQuantity!, 0) / lines.length;
  const percent = Math.floor(share * 100 + 1e-9);
  return coverage.state === 'PARTIAL' ? Math.min(percent, 99) : percent;
}

/**
 * WP11 „niezafakturowana reszta”: `uninvoicedLines` of the correction state
 * and `unsettledLines` of the ZK settlement — lines with an uninvoiced
 * remainder and their quantities (null = unknown, never zero).
 */
export function normalizeRemainderLines(raw: unknown): InvoiceCoverageLine[] {
  return asArray(raw, (item): InvoiceCoverageLine => {
    const row = asRecord(item);
    return {
      id: asString(row?.id),
      name: asOptionalString(row?.name) ?? null,
      unit: asOptionalString(row?.unit) ?? null,
      orderedQuantity: quantity(row?.orderedQuantity),
      invoicedQuantity: quantity(row?.invoicedQuantity),
      remainingQuantity: quantity(row?.remainingQuantity),
    };
  });
}

/** Coverage of one order line (ZK OrderProduct / ZD item) by its id. */
export function coverageLineById(coverage: InvoiceCoverage | null | undefined, id: string): InvoiceCoverageLine | undefined {
  return coverage?.lines.find((line) => line.id === id);
}
